import React, { useEffect, useRef, useState } from "react";
import "./index.less";
import { Button, Popover } from "antd";
import { CameraIcon, HeadPhonesIcon, MicroPhoneIcon } from "../../SvgIconCom/ChatBottomToolIcon";
import {
    ChannelItem,
    useChannelRoomStore,
    useChannelStore,
    useGlobalStore,
    usePreferencesStore,
    useUserStore,
} from "@obsbot/remo-stores";
import { ConnectStatus } from "./ConnectStatus";
import { PreviewCameraModal } from "../../PreviewCameraModal";
import { IServiceVideoChat } from "@obsbot/remo-services";
// import { useTranslate } from "@obsbot/remo-i18n";
import { eventBus } from "@obsbot/remo-utils";
import { BottomToolsSetting } from "./SettingPanel";
import { useTranslate } from "@obsbot/remo-i18n";
export interface BottomToolsProps {
    rtc?: IServiceVideoChat;
    connectStatus?: "connecting" | "no_connected" | "connected" | "disconnecting";
    onHandleLeave?: () => void;
    toSetting?: (toVideo?: boolean) => void;
}

export const BottomTools: React.FC<BottomToolsProps> = ({
    onHandleLeave,
    rtc: passRTC,
    toSetting,
}) => {
    const t = useTranslate();
    const { currentUser, updateCurrentUser } = useUserStore();
    const { updateUserState, userState } = useChannelStore();
    const { isJoinedRTC, leaveRoom, rtc, isLoadingRTC, isEnterRoom, updateIsEnterRoom } =
        useChannelRoomStore();
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const [showRightMenu, setShowRightMenu] = useState<string>();
    const [showPreviewCam, setShowPreviewCam] = useState(false);
    const channelItem = useRef<ChannelItem | null>(null);
    const [isFirstEnter, setIsFirstEnter] = useState(true);

    useEffect(() => {
        if (!isJoinedRTC) {
            setIsFirstEnter(true);
        }
    }, [isJoinedRTC]);

    const avatar = rtc && rtc.getAvatar(currentUser?.userUUID || "");

    useEffect(() => {
        const previewCamHandler = ({
            isPreview,
            channelItem: channel,
        }: {
            isPreview: boolean;
            channelItem?: ChannelItem;
        }): void => {
            if (preferenceStore?.alwaysPreviewCam && preferenceStore?.alwaysVideo) {
                setShowPreviewCam(isPreview);
            } else {
                setShowPreviewCam(false);
                if (!isJoinedRTC) {
                    eventBus.emit("confirm-preview-camera", channel);
                }
            }
            if (channel) {
                channelItem.current = channel;
            }
        };
        eventBus.on("preview-camera", previewCamHandler);
        return () => {
            eventBus.off("preview-camera", previewCamHandler);
        };
    }, [isJoinedRTC, preferenceStore]);

    // 主要用于监测用户修改了自己麦克风的状态去修改耳机的状态
    useEffect(() => {
        const showMic = Boolean(userState?.mic);
        if (showMic && !userState?.isListen) {
            updateCurrentUser({ isListen: showMic });
            updateUserState({ isListen: showMic });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState?.mic]);

    // 当关闭扬声器时关闭麦克风
    useEffect(() => {
        const isListen = Boolean(userState?.isListen);
        const showMic = Boolean(userState?.mic);
        if (!isListen && showMic) {
            updateUserState({ mic: false });
            updateCurrentUser({ mic: false });
        }
        if (rtc && (rtc as any).enabledListen !== undefined) {
            (rtc as any).enabledListen = isListen;
        }
    }, [userState.isListen, rtc]);

    const changeCamera = (isForce?: boolean): void => {
        if (!isJoinedRTC && !isForce) {
            eventBus.emit("show-message", {
                message: t("enterChannelFirst"),
                type: "warning",
            });
            return;
        }
        if (!userState?.camera && preferenceStore?.alwaysPreviewCam && !isForce) {
            setShowPreviewCam(true);
            return;
        }
        // 获取自己的uid，开启关闭摄像头
        const showCam = Boolean(userState?.camera);
        rtc?.setVideoMirror(preferenceStore.videoMirror);
        avatar?.enableCamera(!showCam);
        updateCurrentUser({ camera: !showCam });
        updateUserState({ camera: !showCam });
    };
    const changeMicroPhone = (): void => {
        if (!isJoinedRTC) {
            eventBus.emit("show-message", {
                message: t("enterChannelFirst"),
                type: "warning",
            });
            return;
        }
        const showMic = Boolean(userState?.mic);
        // 判断现在耳机和麦克风是否同时打开，那么现在是要关闭麦克风，耳机将不再控制麦克风
        const controlMic = showMic && userState.isListen;
        avatar?.enableMic(!showMic);
        updateCurrentUser({ mic: !showMic });
        updateUserState({ mic: !showMic, controlMic: !controlMic });
    };
    const changeHeadPhones = (): void => {
        if (!isJoinedRTC) {
            eventBus.emit("show-message", {
                message: t("enterChannelFirst"),
                type: "warning",
            });
            return;
        }
        // 关闭耳机时，同时关闭麦克风，反之亦然
        updateCurrentUser({
            isListen: !userState?.isListen,
            mic: userState.controlMic ? !userState?.isListen : userState.mic,
        });
        updateUserState({
            isListen: !userState?.isListen,
            mic: userState.controlMic ? !userState?.isListen : userState.mic,
        });
    };
    useEffect(() => {
        avatar?.enableRemoteAudio(userState?.isListen ?? false);
    }, [userState.isListen, avatar]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (event.type === "contextmenu") {
                event.preventDefault();
                return;
            }
            // 检查点击事件是否发生在 Popover 浮层内部
            // Popover 的浮层是挂载到 document.body 上的，通过 classNames={{ root: "context-setting-menu" }} 可以找到它
            const popoverElement = document.querySelector(".ant-popover.context-setting-menu"); // 注意：Ant Design 会自动添加 .ant-popover 类
            // 检查点击是否发生在 Popover 浮层内部 或 触发器按钮内部
            // 这里我们没有直接的触发器 ref，因为 trigger={["contextMenu"]} 会自动处理
            // 只要点击不是发生在 Popover 浮层内，就关闭
            if (showRightMenu && popoverElement && !popoverElement.contains(event.target as Node)) {
                setShowRightMenu(""); // 关闭 Popover
            }
        };

        // 只有当 Popover 显示时才添加监听器
        if (showRightMenu) {
            document.addEventListener("click", handleClickOutside);
            // 也可以监听 contextmenu 事件，确保右键点击其他地方也能关闭
            document.addEventListener("contextmenu", handleClickOutside);
        }

        // 清理函数：在组件卸载或 showRightMenu 变为 false 时移除监听器
        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("contextmenu", handleClickOutside);
        };
    }, [showRightMenu]);

    const handleMouseDown = (type: string, e: React.MouseEvent<HTMLElement>): void => {
        e.preventDefault();
        // 未加入房间，不显示右键菜单
        if (e.button === 2 && rtc?.isJoinedRoom) {
            setShowRightMenu(showRightMenu === type ? "" : type);
        }
    };

    useEffect(() => {
        if (!isJoinedRTC) {
            updateIsEnterRoom(false);
            return;
        }

        if (isEnterRoom) {
            return;
        }
        updateIsEnterRoom(true);

        if (preferenceStore.alwaysVideo) {
            avatar?.enableCamera(true);
            updateCurrentUser({ camera: true, isListen: true });
            updateUserState({ camera: true, isListen: true });
        } else {
            avatar?.enableMic(true);
            updateCurrentUser({ mic: true, isListen: true });
            updateUserState({ mic: true, isListen: true });
        }
    }, [
        isJoinedRTC,
        preferenceStore.alwaysVideo,
        avatar,
        updateUserState,
        updateCurrentUser,
        isEnterRoom,
        updateIsEnterRoom,
    ]);

    // 处理拔出的情况, 目前处理逻辑是关闭摄像头
    useEffect(() => {
        const handleCameraMoved = (deviceId: string | undefined): void => {
            if (deviceId === undefined) {
                avatar?.enableCamera(false);
                updateCurrentUser({ camera: false });
                updateUserState({ camera: false });
            }
        };
        rtc?.events.on("camera-changed", handleCameraMoved);
        return () => {
            rtc?.events.off("camera-changed", handleCameraMoved);
        };
    }, [rtc, avatar, updateCurrentUser, updateUserState]);

    return (
        <div className="bottom-tool" onContextMenu={e => e.preventDefault()}>
            {isJoinedRTC && (
                <ConnectStatus
                    connectStatus={
                        isJoinedRTC ? (isLoadingRTC ? "connecting" : "connected") : "no_connected"
                    }
                    delay={currentUser?.networkQuality.delay}
                    leaveRoom={leaveRoom}
                    onHandleLeave={onHandleLeave}
                />
            )}
            <Popover
                arrow={false}
                classNames={{
                    root: "context-setting-menu",
                    body: "context-setting-menu-body",
                }}
                content={
                    toSetting &&
                    rtc &&
                    showRightMenu && (
                        <BottomToolsSetting
                            rightMenuType={showRightMenu}
                            rtc={rtc}
                            toSetting={toSetting}
                        />
                    )
                }
                getPopupContainer={() => document.body}
                open={Boolean(showRightMenu)}
                placement="topRight"
                style={{
                    position: "absolute",
                    top: 660,
                }}
                trigger={["contextMenu"]}
            ></Popover>
            <div className="tool-btns">
                <Button
                    icon={<CameraIcon active={isJoinedRTC ? userState?.camera : false} />}
                    size="large"
                    type="text"
                    onClick={() => changeCamera()}
                    onContextMenu={handleMouseDown.bind(this, "camera")}
                ></Button>
                <Button
                    icon={<MicroPhoneIcon active={isJoinedRTC ? userState?.mic : false} />}
                    type="text"
                    onClick={changeMicroPhone}
                    onMouseDown={handleMouseDown.bind(this, "microphone")}
                ></Button>
                <Button
                    icon={<HeadPhonesIcon active={isJoinedRTC ? userState?.isListen : false} />}
                    size="large"
                    type="text"
                    onClick={changeHeadPhones}
                    onMouseDown={handleMouseDown.bind(this, "headphones")}
                ></Button>
            </div>
            {showPreviewCam && (
                <PreviewCameraModal
                    confirmClick={() => {
                        if (isFirstEnter) {
                            updateCurrentUser({ isListen: true });
                            updateUserState({ isListen: true });
                        }
                        setIsFirstEnter(false);
                        setShowPreviewCam(false);
                        updateCurrentUser({ camera: true });
                        updateUserState({ camera: true });
                        if (!isJoinedRTC) {
                            eventBus.emit("confirm-preview-camera", channelItem.current);
                        }
                    }}
                    rtc={passRTC || rtc!}
                    setVisible={setShowPreviewCam}
                    visible={showPreviewCam}
                    onClickClose={() => {
                        setIsFirstEnter(false);
                    }}
                />
            )}
        </div>
    );
};
